import type { HeaderContextData } from '@portal/state/context/HeaderContext';
import Head from 'next/head';
import { PageTranslation } from 'schema/studio/studio';
import { Header } from 'shared/components/Header/Header';
import Footer from 'shared/studio/Footer/Footer';
import { CanonicalLink } from 'shared/layouts/CanonicalLink';
import { getCanonicalLink } from 'shared/utils/getCanonicalLink';
import { FooterV3Result } from 'types/studio/footer';
import { useLocale } from 'shared/hooks/useLocale';
import type { HeaderBackground } from 'types/studio/header';
import { AlternateLinks } from './AlternateLinks';

type Props = {
  title: string;
  coverImage?: string;
  headerBackground?: HeaderBackground;
  footerData: FooterV3Result | null;
  headerData: HeaderContextData;
  newThemeProgressBar?: boolean;
  headerLinks?: React.ReactNode | React.ReactNode[];
  pageTranslations?: PageTranslation[];
  hasProgressBar?: boolean;
  children: React.ReactNode;
  description?: string;
  structuredSchemaData?: { jsonSchemaData: string; schemaType: string }[];
  canonicalLinkData?: {
    external?: string;
    internal?: { slug?: { current?: string } };
  };
};

const PageHeaderFooterLayout = ({
  title,
  coverImage,
  footerData,
  headerBackground,
  headerData,
  headerLinks,
  pageTranslations,
  hasProgressBar,
  newThemeProgressBar,
  description,
  children,
  structuredSchemaData,
  canonicalLinkData
}: Props) => {
  const locale = useLocale();

  const localCanonicalLink = canonicalLinkData?.internal?.slug?.current;
  const externalCanonical = canonicalLinkData?.external;
  const canonicalLink =
    externalCanonical || (localCanonicalLink ? getCanonicalLink(localCanonicalLink, locale) : '');

  return (
    <>
      <Head>
        <title>{title || 'AG1 by Athletic Greens® - Official Site'}</title>
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:image" content={coverImage} />
        {description && (
          <>
            <meta property="og:description" content={description} />
            <meta name="description" content={description} />
          </>
        )}
        {headerLinks}
        {structuredSchemaData?.map((schema: { jsonSchemaData: string; schemaType: string }) => (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: schema.jsonSchemaData
            }}
            key={schema.schemaType}
          />
        ))}
      </Head>
      {canonicalLink && <CanonicalLink href={canonicalLink} />}
      <AlternateLinks translations={pageTranslations} />

      <main data-content="main">
        {headerBackground && (
          <Header
            desktopLayout={headerBackground === 'transparent' ? 'transparentWhite' : 'white'}
            mobileLayout={headerBackground === 'transparent' ? 'transparentWhite' : 'white'}
            header={headerData.header}
            newThemeProgressBar={newThemeProgressBar}
            logoSVG={headerData.logoSVG}
            hasProgressBar={hasProgressBar}
            translations={pageTranslations}
            activeBanner={null}
            visibleOnPageLoad
          />
        )}
        {children}
      </main>
      {footerData && (
        <Footer data={footerData} pageTranslations={pageTranslations as PageTranslation[]} />
      )}
    </>
  );
};

export default PageHeaderFooterLayout;
